// Import types
import type { LearningCollection, CollectionProgress } from '../types';

// Smart API base URL detection
const getApiBaseUrl = () => {
  // If explicitly set, use it
  if (import.meta.env.VITE_VIAPHOTON_API_URL) {
    console.log('🌐 Using explicit API URL:', import.meta.env.VITE_VIAPHOTON_API_URL);
    return import.meta.env.VITE_VIAPHOTON_API_URL;
  }
  
  // In development, use the proxy
  if (import.meta.env.DEV) {
    console.log('🌐 Development mode: using proxy /api');
    return '/api';
  }
  
  // In production, try to construct the API URL
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // If it's localhost or a known development domain, use proxy
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('🌐 Localhost detected: using proxy /api');
      return '/api';
    }
    
    // For production, try common API patterns
    const possibleUrls = [
      `${protocol}//api.${hostname}`,
      `${protocol}//${hostname}/api`,
      `${protocol}//${hostname}:3001/api`,
      `${protocol}//${hostname.replace('www.', '')}/api`
    ];
    
    console.log('🌐 Production mode: trying API URLs:', possibleUrls);
    console.log('🌐 Current location:', window.location.href);
    
    // Use the same domain with /api path as fallback
    // This should work if your API is served from the same domain
    return '/api';
  }
  
  // Final fallback
  console.log('🌐 Using final fallback: /api');
  return '/api';
};

// Normalize base URL to avoid missing \/api prefix locally
const normalizeApiBaseUrl = (baseUrl: string): string => {
  if (!baseUrl) return '/api';
  // Trim trailing slash for consistency
  let normalized = baseUrl.replace(/\/$/, '');

  // If we're pointing directly at the backend origin (localhost:3001 or kb-api.*)
  // make sure the base includes /api, since backend mounts routes under /api
  const needsApiSuffix = /localhost:3001$/.test(normalized) || /kb-api\./.test(normalized);
  if (needsApiSuffix && !/\/api$/.test(normalized)) {
    normalized += '/api';
  }

  return normalized;
};

export const API_BASE_URL = normalizeApiBaseUrl(getApiBaseUrl());

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: string;
  status: 'operational' | 'beta' | 'maintenance';
  icon: string;
  features: string[];
  users: string[];
  metrics: {
    [key: string]: string;
  };
  url?: string;
  slackChannels?: string[];
  contactPerson?: {
    name: string;
    email: string;
    role: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface StepReference {
  id: string;
  title: string;
  url?: string;
  documentId?: string;
  type: 'document' | 'external-link' | 'internal-link';
}

export interface StepMetadata {
  materials?: string[];
  tools?: string[];
  warnings?: string[];
  cautions?: string[];
  prerequisites?: string[];
  references?: StepReference[];
  duration?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  location?: string;
  quantity?: string;
}

export interface StepSubsection {
  id: string;
  title?: string;
  content: string;
  type: 'text' | 'list' | 'warning' | 'note' | 'image' | 'table';
  order: number;
  items?: string[]; // For list type
}

export interface TutorialStep {
  id: string;
  title: string;
  content: string;
  type: 'text' | 'video' | 'code' | 'quiz' | 'pdf' | 'work-instruction-step' | 'safety-warning' | 'material-list';
  order: number;
  videoUrl?: string;
  pdfUrl?: string;
  pdfFile?: File | null;
  questions?: QuizQuestion[];
  metadata?: StepMetadata;
  subsections?: StepSubsection[];
}

export interface QuizQuestion {
  id: string;
  question: string;
  title: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export type ContentType = 'guide' | 'course' | 'presentation';

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  type: ContentType;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  category: string;
  product?: string | null;
  language?: 'en' | 'es';
  layout?: 'steps' | 'continuous'; // Layout mode: step-by-step navigation or continuous scroll
  steps: TutorialStep[];
  authorId: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  roles: string[]; // Array of roles: 'admin', 'instructor', 'learner', 'manager'
  avatar?: string;
  preferences: {
    theme: 'light' | 'dark' | 'system';
    notifications: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  role?: 'user' | 'admin';
}

export interface AuthResponse {
  user: User;
  token: string;
}

/**
 * Decode JWT token without verification (to check expiration)
 */
function decodeJWT(token: string): { exp?: number; iat?: number; userId?: string } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 */
function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true; // If we can't decode or no expiration, consider it expired
  }
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decoded.exp * 1000;
  const now = Date.now();
  const isExpired = now >= expirationTime;
  
  if (isExpired) {
    const timeSinceExpiry = Math.floor((now - expirationTime) / 1000);
    console.warn(`⚠️ Token expired ${timeSinceExpiry}s ago. Clearing token.`);
  }
  
  return isExpired;
}

/**
 * Get a valid token from localStorage, clearing it if expired
 * This is a helper for components that read tokens directly
 */
export function getValidToken(): string | null {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    console.warn('⚠️ Token from localStorage is expired. Clearing it.');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return null;
  }
  
  return token;
}

class ApiService {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    const storedToken = localStorage.getItem('auth_token');
    
    // Check if stored token is expired before using it
    if (storedToken && isTokenExpired(storedToken)) {
      console.warn('⚠️ Stored token is expired. Clearing it.');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.token = null;
    } else {
      this.token = storedToken;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, any> } = {}
  ): Promise<ApiResponse<T>> {
    let url = `${this.baseURL}${endpoint}`;
    
    // Handle query parameters
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Use the stored authentication token, but check expiration first
    if (this.token) {
      if (isTokenExpired(this.token)) {
        // Token expired, clear it and throw error
        this.logout();
        throw new Error('Token expired. Please log in again.');
      }
      (headers as any).Authorization = `Bearer ${this.token}`;
    }

    try {
      // Debug logging
      console.log('🌐 API Request:', {
        url,
        method: options.method || 'GET',
        headers,
        baseURL: this.baseURL,
        endpoint,
        hasToken: !!this.token
      });

      // Remove params from options since we've already processed them
      const { params, ...fetchOptions } = options;
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      console.log('📡 API Response:', {
        url,
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('❌ Non-JSON response received:', {
          url,
          contentType,
          text: text.substring(0, 200) + (text.length > 200 ? '...' : '')
        });
        throw new Error(`Expected JSON but received ${contentType}. Response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {
        // Create an error object that preserves the response data
        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        (error as any).response = { data };
        throw error;
      }

      console.log('✅ API Success:', { url, data });
      return data;
    } catch (error) {
      console.error('❌ API request failed:', {
        url,
        error: error instanceof Error ? error.message : error,
        baseURL: this.baseURL,
        endpoint
      });
      throw error;
    }
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.data) {
      this.token = response.data.token;
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data!;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.data) {
      this.token = response.data.token;
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data!;
  }

  async getProfile(): Promise<User> {
    const response = await this.request<User>('/auth/profile');
    return response.data!;
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  isAuthenticated(): boolean {
    if (!this.token) return false;
    
    // Check if token is expired
    if (isTokenExpired(this.token)) {
      this.logout();
      return false;
    }
    
    return true;
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Progress tracking methods
  async updateProgress(tutorialId: string, stepId: string, timeSpent: number = 0, isCompleted: boolean = false): Promise<void> {
    await this.request('/user-management/progress', {
      method: 'POST',
      body: JSON.stringify({
        tutorialId,
        stepId,
        timeSpent,
        isCompleted
      }),
    });
  }

  async getProgress(tutorialId: string): Promise<any> {
    const response = await this.request(`/user-management/progress/${tutorialId}`);
    return response.data;
  }

  // Product methods

  async getProducts(_page: number = 1, _limit: number = 10): Promise<{
    products: Product[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Product[]>('/products', {
      method: 'GET',
    });

    return {
      products: response.data || [],
      pagination: response.pagination,
    };
  }

  async getProduct(id: string): Promise<Product> {
    const response = await this.request<Product>(`/products/${id}`);
    return response.data!;
  }

  async getProductsByCategory(category: string, _page: number = 1, _limit: number = 10): Promise<{
    products: Product[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Product[]>(`/products/category/${category}`, {
      method: 'GET',
    });

    return {
      products: response.data || [],
      pagination: response.pagination,
    };
  }

  async createProduct(productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<{
    success: boolean;
    data: Product;
    message: string;
  }> {
    const response = await this.request<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(productData)
    });
    return response as {
      success: boolean;
      data: Product;
      message: string;
    };
  }

  async updateProduct(id: string, productData: Partial<Product>): Promise<{
    success: boolean;
    data: Product;
    message: string;
  }> {
    const response = await this.request<Product>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    });
    return response as {
      success: boolean;
      data: Product;
      message: string;
    };
  }

  async deleteProduct(id: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.request<null>(`/products/${id}`, {
      method: 'DELETE'
    });
    return response as {
      success: boolean;
      message: string;
    };
  }

  async bulkDeleteProducts(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data?: {
      deleted: number;
      failed: string[];
    };
  }> {
    const response = await this.request<{
      deleted: number;
      failed: string[];
    }>('/products/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids })
    });
    return response as {
      success: boolean;
      message: string;
      data?: {
        deleted: number;
        failed: string[];
      };
    };
  }

  // Tutorial methods
  async getTutorials(page: number = 1, limit: number = 10, language: string = 'en'): Promise<{
    tutorials: Tutorial[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Tutorial[]>('/tutorials', {
      method: 'GET',
      params: { page, limit, language }
    });

    return {
      tutorials: response.data || [],
      pagination: response.pagination,
    };
  }

  async getTutorial(id: string): Promise<Tutorial> {
    const response = await this.request<Tutorial>(`/tutorials/${id}`);
    return response.data!;
  }

  // Alias for getTutorial
  async getTutorialById(id: string): Promise<Tutorial> {
    return this.getTutorial(id);
  }

  async getTutorialsByCategory(category: string, _page: number = 1, _limit: number = 10): Promise<{
    tutorials: Tutorial[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Tutorial[]>(`/tutorials/category/${category}`, {
      method: 'GET',
    });

    return {
      tutorials: response.data || [],
      pagination: response.pagination,
    };
  }

  async createTutorial(tutorial: Omit<Tutorial, 'id' | 'createdAt' | 'updatedAt' | 'authorId'>): Promise<Tutorial> {
    const response = await this.request<Tutorial>('/tutorials', {
      method: 'POST',
      body: JSON.stringify(tutorial),
    });

    return response.data!;
  }

  async createPdfGeneratedTutorial(tutorial: Omit<Tutorial, 'id' | 'createdAt' | 'updatedAt' | 'authorId'>): Promise<Tutorial> {
    const response = await this.request<Tutorial>('/tutorials/pdf-generated', {
      method: 'POST',
      body: JSON.stringify(tutorial),
    });

    return response.data!;
  }

  // Draft management methods
  async getDrafts(): Promise<Tutorial[]> {
    const response = await this.request<Tutorial[]>('/tutorials/drafts');
    return response.data || [];
  }

  async approveDraft(draftId: string): Promise<void> {
    await this.request(`/tutorials/drafts/${draftId}/approve`, {
      method: 'PUT',
    });
  }

  async deleteDraft(draftId: string): Promise<void> {
    await this.request(`/tutorials/drafts/${draftId}`, {
      method: 'DELETE',
    });
  }

  async updateTutorial(id: string, tutorial: Partial<Tutorial>): Promise<Tutorial> {
    const response = await this.request<Tutorial>(`/tutorials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tutorial),
    });

    return response.data!;
  }

  async deleteTutorial(id: string): Promise<void> {
    await this.request(`/tutorials/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteTutorials(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data?: {
      deleted: number;
      failed: string[];
    };
  }> {
    const response = await this.request<{
      deleted: number;
      failed: string[];
    }>('/tutorials/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids })
    });
    return response as {
      success: boolean;
      message: string;
      data?: {
        deleted: number;
        failed: string[];
      };
    };
  }

  // User Management Methods
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }): Promise<ApiResponse<{
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role && params.role !== 'all') queryParams.append('role', params.role);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';
    
    return await this.request<{
      users: User[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(endpoint);
  }

  async createUser(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    roles: string[];
  }): Promise<ApiResponse<User>> {
    return await this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: string, userData: any): Promise<ApiResponse<User>> {
    return await this.request<User>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: string): Promise<void> {
    await this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async resetPassword(userId: string, newPassword: string): Promise<void> {
    await this.request(`/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  }

  // Tutorial Methods
  async getTutorialOverview(tutorialId: string): Promise<ApiResponse<any>> {
    return await this.request(`/tutorials/${tutorialId}/overview`);
  }

  // Translation Methods
  async getTranslationLanguages(tutorialId: string): Promise<ApiResponse<any>> {
    return await this.request(`/translation/languages/${tutorialId}`);
  }

  async getTutorialInLanguage(tutorialId: string, language: string): Promise<ApiResponse<any>> {
    return await this.request(`/translation/tutorial/${tutorialId}/language/${language}`);
  }

  async getTranslationProgress(translationId: string): Promise<ApiResponse<any>> {
    return await this.request(`/translation/progress/${translationId}?_t=${Date.now()}`);
  }

  // Category Methods

  // Glossary Methods
  async getGlossary(activeOnly: boolean = false, forHighlighting: boolean = false): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (activeOnly) params.append('activeOnly', 'true');
    if (forHighlighting) params.append('forHighlighting', 'true');
    const queryString = params.toString();
    return await this.request(`/glossary${queryString ? `?${queryString}` : ''}`);
  }

  // Extract technical terms from tutorial (for manual extraction)
  async extractGlossaryTerms(tutorial: any): Promise<ApiResponse<{ keywords: any[] }>> {
    return await this.request('/glossary/extract', {
      method: 'POST',
      body: JSON.stringify({ tutorial })
    });
  }

  // Submit glossary entries for approval
  async submitGlossaryEntries(entries: Array<{ term: string; definition: string; category?: string; aliases?: string[]; tags?: string[] }>): Promise<ApiResponse<any>> {
    return await this.request('/glossary/submit', {
      method: 'POST',
      body: JSON.stringify({ entries })
    });
  }

  // Approve glossary entries (admin/instructor only)
  async approveGlossaryEntries(ids: string[]): Promise<ApiResponse<any>> {
    return await this.request('/glossary/approve', {
      method: 'POST',
      body: JSON.stringify({ ids })
    });
  }

  // Reject glossary entries (admin/instructor only)
  async rejectGlossaryEntries(ids: string[]): Promise<ApiResponse<any>> {
    return await this.request('/glossary/reject', {
      method: 'POST',
      body: JSON.stringify({ ids })
    });
  }

  // User Management Methods
  async enrollInCourse(tutorialId: string): Promise<ApiResponse<any>> {
    return await this.request('/user-management/enroll', {
      method: 'POST',
      body: JSON.stringify({ tutorialId }),
    });
  }

  async getEnrollments(): Promise<ApiResponse<any[]>> {
    return await this.request('/user-management/enrollments');
  }

  // Hierarchy Methods
  async getHierarchyTree(): Promise<ApiResponse<any>> {
    return await this.request('/hierarchy/tree');
  }

  async getCollections(): Promise<ApiResponse<any[]>> {
    return await this.request('/hierarchy/collections');
  }

  async getCollection(collectionId: string): Promise<ApiResponse<any>> {
    return await this.request(`/hierarchy/collections/${collectionId}`);
  }

  async createCollection(data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    sortOrder?: number;
  }): Promise<ApiResponse<any>> {
    return await this.request('/hierarchy/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCollection(collectionId: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return await this.request(`/hierarchy/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCollection(collectionId: string): Promise<ApiResponse<void>> {
    return await this.request(`/hierarchy/collections/${collectionId}`, {
      method: 'DELETE',
    });
  }

  async getCategories(collectionId?: string): Promise<ApiResponse<any[]>> {
    const url = collectionId 
      ? `/hierarchy/categories?collectionId=${collectionId}`
      : '/hierarchy/categories';
    return await this.request(url);
  }

  async createCategory(data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    collectionId?: string;
    parentCategoryId?: string;
    sortOrder?: number;
  }): Promise<ApiResponse<any>> {
    return await this.request('/hierarchy/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(categoryId: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return await this.request(`/hierarchy/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(categoryId: string): Promise<ApiResponse<void>> {
    return await this.request(`/hierarchy/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  async moveTutorial(tutorialId: string, collectionId?: string, categoryId?: string): Promise<ApiResponse<void>> {
    return await this.request(`/hierarchy/tutorials/${tutorialId}/move`, {
      method: 'PUT',
      body: JSON.stringify({ collectionId, categoryId }),
    });
  }

  async reorderHierarchy(updates: Array<{
    id: string;
    type: 'collection' | 'category' | 'tutorial';
    sortOrder: number;
    collectionId?: string;
    categoryId?: string;
    parentCategoryId?: string;
  }>): Promise<ApiResponse<void>> {
    return await this.request('/hierarchy/reorder', {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    });
  }

  // Search method
  async search(query: string, limit: number = 50): Promise<ApiResponse<{
    results: Array<{
      id: string;
      title: string;
      content: string;
      url?: string;
      type: 'tutorial' | 'category' | 'glossary';
      relevanceScore?: number;
      category?: string;
      difficulty?: string;
      description?: string;
      term?: string;
      definition?: string;
    }>;
    total: number;
    tutorialCount: number;
    categoryCount: number;
    glossaryCount: number;
  }>> {
    return await this.request(`/search/all?query=${encodeURIComponent(query)}&limit=${limit}`);
  }

  // Generic request method for custom endpoints
  async makeRequest<T>(endpoint: string, options: {
    method?: string;
    body?: any;
  } = {}): Promise<ApiResponse<T>> {
    return await this.request<T>(endpoint, options);
  }

  // Learning Collections API methods
  async getLearningCollections(): Promise<ApiResponse<LearningCollection[]>> {
    return await this.request<LearningCollection[]>('/collections');
  }

  async getLearningCollection(id: string): Promise<ApiResponse<LearningCollection>> {
    return await this.request<LearningCollection>(`/collections/${id}`);
  }

  async createLearningCollection(data: {
    name: string;
    description?: string;
    icon?: string;
  }): Promise<ApiResponse<LearningCollection>> {
    return await this.request<LearningCollection>('/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLearningCollection(
    id: string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
    }
  ): Promise<ApiResponse<LearningCollection>> {
    return await this.request<LearningCollection>(`/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLearningCollection(id: string): Promise<ApiResponse<void>> {
    return await this.request<void>(`/collections/${id}`, {
      method: 'DELETE',
    });
  }

  async addTutorialsToCollection(
    collectionId: string,
    tutorialIds: string[]
  ): Promise<ApiResponse<{ added: number; skipped: number; addedIds: string[]; skippedIds: string[] }>> {
    return await this.request(`/collections/${collectionId}/tutorials`, {
      method: 'POST',
      body: JSON.stringify({ tutorialIds }),
    });
  }

  async removeTutorialFromCollection(
    collectionId: string,
    tutorialId: string
  ): Promise<ApiResponse<void>> {
    return await this.request<void>(`/collections/${collectionId}/tutorials/${tutorialId}`, {
      method: 'DELETE',
    });
  }

  async addCategoriesToCollection(
    collectionId: string,
    categoryIds: string[]
  ): Promise<ApiResponse<{ added: number; skipped: number; addedIds: string[]; skippedIds: string[] }>> {
    return await this.request(`/collections/${collectionId}/categories`, {
      method: 'POST',
      body: JSON.stringify({ categoryIds }),
    });
  }

  async removeCategoryFromCollection(
    collectionId: string,
    categoryId: string
  ): Promise<ApiResponse<void>> {
    return await this.request<void>(`/collections/${collectionId}/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  async assignCollectionToUsers(
    collectionId: string,
    userIds: string[]
  ): Promise<ApiResponse<{ assigned: number; skipped: number; assignedIds: string[]; skippedIds: string[] }>> {
    return await this.request(`/collections/${collectionId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
  }

  async unassignCollectionFromUser(
    collectionId: string,
    userId: string
  ): Promise<ApiResponse<void>> {
    return await this.request<void>(`/collections/${collectionId}/assign/${userId}`, {
      method: 'DELETE',
    });
  }

  async getAssignedCollections(): Promise<ApiResponse<LearningCollection[]>> {
    return await this.request<LearningCollection[]>('/collections/assigned');
  }

  async getCollectionProgress(
    collectionId: string,
    userId?: string
  ): Promise<ApiResponse<CollectionProgress>> {
    const url = userId
      ? `/collections/${collectionId}/progress?userId=${userId}`
      : `/collections/${collectionId}/progress`;
    return await this.request<CollectionProgress>(url);
  }

  async getCollectionsByCategory(categoryId: string): Promise<ApiResponse<LearningCollection[]>> {
    return await this.request<LearningCollection[]>(`/collections/category/${categoryId}`);
  }

  async getCollectionsByTutorial(tutorialId: string): Promise<ApiResponse<LearningCollection[]>> {
    return await this.request<LearningCollection[]>(`/collections/tutorial/${tutorialId}`);
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export types for use in components
// Types are already exported above
